import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  pathologistAPI,
  pdfAPI,
  downloadFile,
  licensingAPI,
  LicenseRecord,
} from "../utils/api";
import { Pathologist, ServiceLine, CustomContent } from "../types";
import EditTemplateModal from "../components/EditTemplateModal";

const ServiceLinePage: React.FC = () => {
  const { serviceLine } = useParams<{ serviceLine: string }>();

  // Get API base URL for image paths
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();

  const [pathologists, setPathologists] = useState<Pathologist[]>([]);
  const [filteredPathologists, setFilteredPathologists] = useState<
    Pathologist[]
  >([]);
  const [selectedPathologists, setSelectedPathologists] = useState<
    Pathologist[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [templateContent, setTemplateContent] = useState<CustomContent | null>(
    null
  );

  const [userGroup, setUserGroup] = useState<string | null>(null);
  const [canEditTemplate, setCanEditTemplate] = useState(false);

  // NEW: State filter functionality
  const [availableStates, setAvailableStates] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<string>("");
  const [activeLicenses, setActiveLicenses] = useState<LicenseRecord[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);

  const [loadingLicenses, setLoadingLicenses] = useState(false);

  const [isInstructionsOpen, setIsInstructionsOpen] = useState(true);
  const [refreshingCache, setRefreshingCache] = useState(false);
  const [refreshSuccess, setRefreshSuccess] = useState<string | null>(null);

  const [pdfSuccess, setPdfSuccess] = useState(false);

  // Convert images to base64 using browser cache
  const convertImagesToBase64 = async (
    pathologists: Pathologist[]
  ): Promise<Pathologist[]> => {
    const promises = pathologists.map(async (p) => {
      if (!p.img || p.img.startsWith("data:")) {
        return p; // Already base64 or no image
      }

      try {
        //const response = await fetch(`${API_BASE_URL}${p.img}`, { credentials: 'include' });
        const response = await fetch(p.img);
        const blob = await response.blob();

        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });

        return { ...p, img: base64 };
      } catch (err) {
        console.error(`Failed to convert image for ${p.firstname}:`, err);
        return p;
      }
    });

    return Promise.all(promises);
  };

  // Fetch available states from licensing module
  const fetchStates = async () => {
    try {
      setLoadingStates(true);
      const states = await licensingAPI.getStates();
      setAvailableStates(states);
    } catch (err) {
      console.error("Error fetching states:", err);
    } finally {
      setLoadingStates(false);
    }
  };

  // Fetch active licenses for filtering
  const fetchActiveLicenses = async (state?: string) => {
    if (!state) return;

    try {
      setLoadingLicenses(true); // Start loading
      const licenses = await licensingAPI.getActiveLicenses(state);
      setActiveLicenses(licenses);
    } catch (err) {
      console.error("Error fetching active licenses:", err);
      setActiveLicenses([]);
    } finally {
      setLoadingLicenses(false); // Stop loading
    }
  };

  // Filter pathologists based on selected state and active licenses
  const filterPathologistsByState = (
    pathologistsList: Pathologist[],
    state: string,
    licenses: LicenseRecord[]
  ) => {
    if (!state || licenses.length === 0) {
      return pathologistsList;
    }

    // Create a set of physician names with active licenses in the selected state
    const activePhysiciansInState = new Set(
      licenses
        .filter(
          (license) =>
            license.status.toLowerCase() === "active" &&
            license.state.toLowerCase() === state.toLowerCase()
        )
        .map((license) => license.physician.toLowerCase())
    );

    // Filter pathologists by matching their name with active licenses
    return pathologistsList.filter((pathologist) => {
      const fullName =
        `${pathologist.firstname} ${pathologist.lastname}, ${pathologist.employee_suffix}`.toLowerCase();
      const displayName = pathologist.display_name?.toLowerCase() || "";

      // Check if the pathologist's name matches any active licensed physician
      return (
        activePhysiciansInState.has(fullName) ||
        activePhysiciansInState.has(displayName) ||
        Array.from(activePhysiciansInState).some(
          (licensedName) =>
            licensedName.includes(pathologist.lastname.toLowerCase()) &&
            licensedName.includes(pathologist.firstname.toLowerCase())
        )
      );
    });
  };

  // Handle state filter change
  const handleStateChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const state = e.target.value;
    setSelectedState(state);
    setSelectedPathologists([]);

    if (state) {
      await fetchActiveLicenses(state);
    } else {
      setActiveLicenses([]);
      setFilteredPathologists(pathologists);
      setLoadingLicenses(false);
    }
  };

  // Update filtered pathologists when licenses or selection changes
  useEffect(() => {
    if (selectedState && activeLicenses.length > 0) {
      const filtered = filterPathologistsByState(
        pathologists,
        selectedState,
        activeLicenses
      );
      setFilteredPathologists(filtered);
    } else {
      setFilteredPathologists(pathologists);
    }
  }, [selectedState, activeLicenses, pathologists]);

  // Check if user has access to this service line
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/user-role`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const roleData = await response.json();
          const allowedLines = roleData.salesServiceLines || [];
          
          // Check if user has access to current service line
          if (allowedLines.length > 0 && !allowedLines.includes(serviceLine)) {
            // User doesn't have access - redirect to dashboard
            console.warn(`Access denied to service line: ${serviceLine}`);
            navigate('/dashboard');
            return;
          }
        }
      } catch (err) {
        console.error('Error checking service line access:', err);
      }
    };
    
    checkAccess();
  }, [serviceLine, navigate]);

  const fetchPathologists = async (serviceLineParam: ServiceLine) => {
    try {
      setLoading(true);
      const data = await pathologistAPI.getByServiceLine(serviceLineParam);
      setPathologists(data);
      setFilteredPathologists(data);
    } catch (err) {
      setError("Failed to load pathologists");
      console.error("Error fetching pathologists:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePathologistToggle = (pathologist: Pathologist) => {
    setSelectedPathologists((prev) => {
      const isSelected = prev.find((p) => p.id === pathologist.id);

      if (isSelected) {
        // Remove from selection
        return prev.filter((p) => p.id !== pathologist.id);
      } else if (prev.length < 6) {
        // Add to selection if under limit
        return [...prev, pathologist];
      } else {
        // Show error if trying to select more than 6
        alert("Maximum 6 pathologists can be selected");
        return prev;
      }
    });
  };

  const sortedPathologists = [...filteredPathologists].sort((a, b) => {
    // Sort alphabetically by first name only
    return a.firstname.localeCompare(b.firstname);
  });

  const handleGeneratePDF = async () => {
    if (selectedPathologists.length < 3) {
      alert("Please select at least 3 pathologists");
      return;
    }

    try {
      setGenerating(true);
      setPdfSuccess(false); // Reset success state

      // Convert images to base64 from browser cache
      const pathologistsWithBase64 = await convertImagesToBase64(
        selectedPathologists
      );

      const pdfBlob = await pdfAPI.generate({
        pathologists: pathologistsWithBase64,
        serviceLine: serviceLine as ServiceLine,
      });

      const filename = generateFilename(selectedPathologists);
      downloadFile(pdfBlob, filename);
      
      // Show success message
      setPdfSuccess(true);
      
      // Auto-hide after 5 seconds
      setTimeout(() => {
        setPdfSuccess(false);
      }, 10000);
    } catch (err) {
      setError("Failed to generate PDF");
      console.error("Error generating PDF:", err);
    } finally {
      setGenerating(false);
    }
  };

  const handlePreviewPDF = async () => {
    if (selectedPathologists.length < 3) {
      alert("Please select at least 3 pathologists");
      return;
    }

    try {
      setPreviewing(true);

      // Convert images to base64 from browser cache
      const pathologistsWithBase64 = await convertImagesToBase64(
        selectedPathologists
      );

      const previewBlob = await pdfAPI.preview({
        pathologists: pathologistsWithBase64,
        serviceLine: serviceLine as ServiceLine,
        // customContent removed - backend will fetch from Zoho
      });

      const objectUrl = URL.createObjectURL(previewBlob);
      setPreviewUrl(objectUrl);
    } catch (err) {
      setError("Failed to generate PDF preview");
      console.error("Error generating preview:", err);
    } finally {
      setPreviewing(false);
    }
  };

  const closePreview = () => {
    // Clean up object URL to prevent memory leaks
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };
  
  const handleRefreshCache = async () => {
    try {
      setRefreshingCache(true);
      setRefreshSuccess(null);
      
      const result = await pdfAPI.refreshTemplateCache();
      
      setRefreshSuccess(result.message);
      console.log('✅ Cache refreshed:', result);
      
      // Clear success message after 3 seconds
      setTimeout(() => setRefreshSuccess(null), 3000);
    } catch (err) {
      setError('Failed to refresh template cache');
      console.error('Error refreshing cache:', err);
    } finally {
      setRefreshingCache(false);
    }
  };

  const handleTemplateContentSave = async () => {
    // Refresh template content after save
    try {
      const updatedContent = await pdfAPI.getTemplateContent(
        serviceLine as ServiceLine
      );
      setTemplateContent(updatedContent);
      
      // Auto-refresh PDF template cache after saving
      try {
        await pdfAPI.refreshTemplateCache();
        console.log('✅ PDF template cache auto-refreshed after save');
      } catch (cacheErr) {
        console.warn('⚠️  Failed to auto-refresh cache (non-critical):', cacheErr);
      }
    } catch (err) {
      console.error("Error refreshing template content:", err);
    }
  };

  const loadTemplateContent = async () => {
    try {
      const content = await pdfAPI.getTemplateContent(
        serviceLine as ServiceLine
      );
      setTemplateContent(content);
    } catch (err) {
      console.error("Error loading template content:", err);
    }
  };

  useEffect(() => {
    if (
      serviceLine &&
      ["dermatology", "podiatry", "surgical"].includes(serviceLine)
    ) {
      fetchPathologists(serviceLine as ServiceLine);
      loadTemplateContent();
      fetchStates(); // NEW: Fetch available states on mount
    } else {
      setError("Invalid service line");
      setLoading(false);
    }
  }, [serviceLine]);

  useEffect(() => {
    checkUserPermissions();
  }, []);

  const checkUserPermissions = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/user-role`,
        {
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        const department = data.department;

        setUserGroup(department);
        setCanEditTemplate(data.canEditTemplate);

        console.log("🔐 User permissions:", {
          department: department || "none",
          canEditTemplate: data.canEditTemplate,
        });
      } else {
        setCanEditTemplate(false);
      }
    } catch (err) {
      console.error("Error checking user permissions:", err);
      setCanEditTemplate(false);
    }
  };

  const generateFilename = (pathologists: Pathologist[]) => {
    // Build the filename with pathologist names and credentials
    const pathologistNames = pathologists
      .map((p) => `${p.lastname}, ${p.employee_suffix}`)
      .join("_");

    return `RPG_${pathologistNames}.pdf`;
  };

  const getServiceLineTitle = (sl: string) => {
    const titles = {
      dermatology: "DERMATOPATHOLOGISTS",
      podiatry: "PODIATRIC PATHOLOGISTS",
      surgical: "SURGICAL PATHOLOGISTS",
    };
    return titles[sl as ServiceLine] || sl;
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
          <br />
          <button
            className="btn btn-backtodashboard-servicelinepage"
            onClick={() => navigate("/dashboard")}
          >
            ERROR!!! Please logout and relogin again.
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100">
      {/* Background Video */}
      <video
        className="video-background"
        autoPlay
        muted
        playsInline
        loop
        src="https://www.sagisdx.com/wp-content/uploads/2023/04/sagis-swirl-bg-3.mp4"
      />

      <div className="container">
        <div className="container-header-servicelinepage">
          <h2
            className="text-serviceline-header"
            style={{
              color:
                serviceLine === "dermatology"
                  ? "#58ab40"
                  : serviceLine === "podiatry"
                  ? "#4b8579"
                  : "#53575b",
            }}
          >
            {getServiceLineTitle(serviceLine || "")}
          </h2>

          <button
            className="btn btn-backtodashboard-servicelinepage-top"
            onClick={() => {
              window.scrollTo(0, 0); // scroll to top
              navigate("/dashboard"); // then navigate
            }}
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Instructions Accordion */}
        <div className="accordion mb-3" id="instructionsAccordion">
          <div className="accordion-item">
            <h2 className="accordion-header" id="headingInstructions">
              <button
                className={`accordion-button ${isInstructionsOpen ? '' : 'collapsed'} instructions-accordion-header`}
                type="button"
                onClick={() => setIsInstructionsOpen(!isInstructionsOpen)}
                aria-expanded={isInstructionsOpen}
                aria-controls="collapseInstructions"
              >
                <div>
                  <div className="instructions-main-title">How to Build Your Pathologist PDF</div>
                  <div className="instructions-header-subtitle">Follow the steps below</div>
                </div>
              </button>
            </h2>
            <div
              id="collapseInstructions"
              className={`accordion-collapse collapse ${isInstructionsOpen ? 'show' : ''}`}
              aria-labelledby="headingInstructions"
            >
              <div className="accordion-body instructions-accordion-body">
                <h6 className="instructions-step-title">Step 1: Select Pathologists</h6>
                <p className="instructions-step-content">
                  Select between <strong>3 (minimum)</strong> and <strong>6 (maximum)</strong> pathologists for your PDF.
                </p>
                
                <h6 className="instructions-step-title">Step 2: Click on a Pathologist's Card</h6>
                <ul className="instructions-step-content">
                  <li>Pathologists will appear in the PDF in the order you select them.</li>
                  <li>Use the <strong>State Filter</strong> to show pathologists that are licensed in specific states.</li>
                </ul>
                
                <h6 className="instructions-step-title">Step 3: Generate PDF</h6>
                <ul className="instructions-step-content mb-0">
                  <li>
                    Once you've selected at least 3 pathologists, the <em>Preview PDF</em> and <em>Generate PDF</em> buttons will become active.
                  </li>
                  <li>
                    Click <em>Preview PDF</em> to review before downloading, or <em>Generate PDF</em> to download immediately.
                  </li>
                </ul>
                
                {canEditTemplate && (
                  <div className="instructions-edit-section">
                    <button 
                      className="btn btn-edit-servicelinepage"
                      onClick={() => setIsEditModalOpen(true)}
                    >
                      ✏️ Edit Template Content
                    </button>
                    
                    <button 
                      className="btn btn-update-servicelinepage"
                      onClick={handleRefreshCache}
                      disabled={refreshingCache}
                      style={{ marginLeft: '6px' }}
                    >
                      {refreshingCache ? '🔄 Refreshing...' : '🔄 Update Template Content'}
                    </button>
                    {refreshSuccess && (
                      <div className="alert-update-servicelinepage">
                        ✅ {refreshSuccess}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {selectedState &&
        !loadingLicenses &&
        filteredPathologists.length < 3 ? (
          <div
            className="alert alert-danger alert-box-servicelinepage"
            role="alert"
          >
            <h5 className="alert-heading">
              Insufficient Licensed Pathologists
            </h5>
            <p className="mb-0">
              The selected state does not have the minimum number of licensed
              pathologists to generate a Regional Pathologist PDF. Please visit{" "}
              <a
                href="https://www.sagisdx.com/our-diagnostic-experts/"
                target="_blank"
                rel="noopener noreferrer"
                className="alert-link"
              >
                https://www.sagisdx.com/our-diagnostic-experts/
              </a>{" "}
              to download individual bio sheets for each pathologist.
            </p>
          </div>
        ) : (
          selectedPathologists.length > 0 && (
            <div className={`selectedpathologists-box-serviceline sticky-selected-box ${
              selectedPathologists.length < 3 ? 'insufficient-selection' : ''
            }`}>
              <h5>
                Selected Pathologists ({selectedPathologists.length}/6):
                {selectedPathologists.length < 3 && (
                  <span className="text-warning ms-2">
                    A minimum of THREE pathologists is required to generate a Regional Pathologist PDF. Please visit{" "}
                    <a
                      href="https://www.sagisdx.com/our-diagnostic-experts/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="alert-link"
                    >
                      https://www.sagisdx.com/our-diagnostic-experts/
                    </a>{" "}
                    to download individual bio sheets for each pathologist.
                  </span>
                )}
              </h5>
              <div className="row">
                {selectedPathologists.map((pathologist) => (
                  <div key={pathologist.id} className="col-md-4 mb-2">
                    <small className="text-rpg-primary">
                      ✓ {pathologist.firstname} {pathologist.lastname}
                    </small>
                  </div>
                ))}
              </div>

              <div className="mt-3">
                <button
                  className={`btn ${
                    selectedPathologists.length < 3
                      ? "btn-outline-primary"
                      : "btn-primary"
                  } btn-selected-patho me-2`}
                  onClick={handleGeneratePDF}
                  disabled={generating || selectedPathologists.length < 3}
                >
                  {generating ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Generating...
                    </>
                  ) : (
                    "Generate PDF"
                  )}
                </button>

                <button
                  className={`btn ${
                    selectedPathologists.length < 3
                      ? "btn-outline-success"
                      : "btn-success"
                  } btn-selected-patho me-2`}
                  onClick={handlePreviewPDF}
                  disabled={previewing || selectedPathologists.length < 3}
                >
                  {previewing ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Loading Preview...
                    </>
                  ) : (
                    "Preview PDF"
                  )}
                </button>

                <button
                  className="btn btn-secondary btn-selected-patho me-2"
                  onClick={() => setSelectedPathologists([])}
                >
                  Clear Selection
                </button>

                {pdfSuccess && (
                  <div className="alert-success-content mt-3 mb-0" role="alert">
                    <span className="alert-success-text">Success!</span> Your PDF has been generated and saved to your downloads folder.
                  </div>
                )}
              </div>
            </div>
          )
        )}

        {/* NEW: State Filter */}
        <div className="mb-4">
          <div className="row">
            <div className="col-md-2">
              <label htmlFor="stateFilter" className="form-label">
                <p>Filter by State:</p>
              </label>
              <select
                id="stateFilter"
                className="form-select"
                value={selectedState}
                onChange={handleStateChange}
                disabled={loadingStates}
              >
                <option value="">All States</option>
                {availableStates.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
            {selectedState && (
              <small className="filter-selected-small-text">
                {loadingLicenses ? (
                  <span className="text-muted">
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Loading licenses for {selectedState}...
                  </span>
                ) : (
                  `${filteredPathologists.length} pathologists found with "Active" licenses in ${selectedState}`
                )}
              </small>
            )}
          </div>
        </div>

        {/* Pathologist List */}
        <div className="row pathologist-text-servicelinepage">
          {loadingLicenses ? (
            // Show loading state while fetching licenses
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-success mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h5 className="text-muted">
                Loading pathologists with active licenses in {selectedState}...
              </h5>
            </div>
          ) : sortedPathologists.length === 0 ? (
            <div className="col-12 text-center py-5">
              <h4 className="text-muted">
                {selectedState
                  ? `No pathologists found with active licenses in ${selectedState}`
                  : "No pathologists found for this service line"}
              </h4>
              {selectedState && (
                <button
                  className="btn btn-outline-secondary mb-3"
                  onClick={() => {
                    setSelectedState("");
                    setActiveLicenses([]);
                  }}
                >
                  Clear State Filter
                </button>
              )}
              <br />
            </div>
          ) : (
            sortedPathologists.map((pathologist) => {
              const isSelected = selectedPathologists.find(
                (p) => p.id === pathologist.id
              );

              return (
                <div key={pathologist.id} className="col-lg-6 col-xl-4 mb-3">
                  <div
                    className={`card pathologist-card ${
                      isSelected ? "selected" : ""
                    }`}
                    onClick={() => handlePathologistToggle(pathologist)}
                  >
                    <div className="card-body">
                      <div className="d-flex align-items-start">
                        <div className="me-3">
                          {pathologist.img ? (
                            <div className="doctor-image-container">
                              <img
                                //src={`${API_BASE_URL}${pathologist.img}`}
                                src={pathologist.img}
                                alt={`${pathologist.firstname} ${pathologist.lastname}`}
                                //crossOrigin="use-credentials"
                              />
                            </div>
                          ) : (
                            <div
                              className="doctor-image-container d-flex align-items-center justify-content-center bg-light"
                              style={{ color: "#666" }}
                            >
                              N/A
                            </div>
                          )}
                          {isSelected && (
                            <span className="badge bg-success selected-success-servicelinepage">
                              Selected
                            </span>
                          )}
                        </div>

                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-2">
                            <h6 className="mb-0 me-2 pathologist-name">
                              {pathologist.firstname}{" "}
                              {pathologist.lastname + ","}{" "}
                              {pathologist.employee_suffix}
                            </h6>
                          </div>

                          <div className="doctor-info-servicelinepage text-muted">
                            {pathologist.board_certification && (
                              <div>
                                <strong>Board Certification:</strong>{" "}
                                {pathologist.board_certification}
                              </div>
                            )}
                            {pathologist.fellowship_training && (
                              <div>
                                <strong>Fellowship Training:</strong>{" "}
                                {pathologist.fellowship_training}
                              </div>
                            )}
                            {pathologist.fellowship_training_2 && (
                              <div>
                                <strong>Fellowship Training 2:</strong>{" "}
                                {pathologist.fellowship_training_2}
                              </div>
                            )}
                            {pathologist.residency_training && (
                              <div>
                                <strong>Residency Training:</strong>{" "}
                                {pathologist.residency_training}
                              </div>
                            )}
                            {pathologist.medical_school && (
                              <div>
                                <strong>Medical School:</strong>{" "}
                                {pathologist.medical_school}
                              </div>
                            )}
                            {pathologist.undergraduate && (
                              <div>
                                <strong>Undergraduate:</strong>{" "}
                                {pathologist.undergraduate}
                              </div>
                            )}
                            {pathologist.graduate_school && (
                              <div>
                                <strong>Graduate School:</strong>{" "}
                                {pathologist.graduate_school}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* PDF Preview Modal */}
        {previewUrl && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">PDF Preview</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closePreview}
                  ></button>
                </div>
                <div className="modal-body">
                  <iframe
                    src={previewUrl}
                    className="pdf-preview"
                    title="PDF Preview"
                  />
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-close-pdfpreview"
                    onClick={closePreview}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-download-pdfpreview"
                    onClick={handleGeneratePDF}
                    disabled={generating}
                  >
                    Download PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="row mt-4 mb-4">
          <div className="col-12 text-center">
            <button
              className="btn btn-backtodashboard-servicelinepage-bottom"
              onClick={() => {
                window.scrollTo(0, 0); // scroll to top
                navigate("/dashboard"); // then navigate
              }}
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {/* Edit Template Modal */}
        <EditTemplateModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          serviceLine={serviceLine as ServiceLine}
          onSave={handleTemplateContentSave}
        />
      </div>
    </div>
  );
};

export default ServiceLinePage;